import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { Toggle } from '@actual-app/components/toggle';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type { HuellaGuardada } from '@actual-app/core/server/encryption/cofre-app';

import { color, texto } from '#components/mobile/ui/tokens';

import { CrearPin } from './CrearPin';
import {
  activarHuella,
  cambiarPedirHuellaAlAbrir,
  desactivarHuella,
  huellaDeEsteMovil,
  pedirHuellaAlAbrir,
} from './huella';
import type { ResultadoHuella } from './huella';
import { Enlace } from './PantallaCofre';

/**
 * Ajustes › Cuenta › Huella: activarla en este móvil, pedirla al abrir y
 * quitar la de otros aparatos.
 */
export function AjustesHuella() {
  const { t } = useTranslation();
  const [idLocal, setIdLocal] = useState(() => huellaDeEsteMovil());
  const [alAbrir, setAlAbrir] = useState(() => pedirHuellaAlAbrir());
  const [otras, setOtras] = useState<HuellaGuardada[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // Encender «al abrir» pide antes elegir el PIN; también «Cambiar el PIN».
  const [eligiendoPin, setEligiendoPin] = useState(false);

  async function cargar() {
    const res = await send('huella-lista');
    setOtras((res.huellas ?? []).filter(h => h.id !== huellaDeEsteMovil()));
  }

  useEffect(() => {
    void cargar();
  }, []);

  function mensaje(res: ResultadoHuella) {
    switch (res) {
      case 'ok':
      case 'cancelada':
        return null;
      case 'sin-conexion':
        return t(
          "We couldn't reach the server. Check your connection and try again.",
        );
      case 'demasiadas':
        return t('You already have 10 devices. Remove one to add this one.');
      default:
        return t("That didn't work. Try again.");
    }
  }

  async function alternarHuella(activar: boolean) {
    setOcupado(true);
    setError(null);
    const res = activar ? await activarHuella() : await desactivarHuella();
    setOcupado(false);
    setError(mensaje(res));
    setIdLocal(huellaDeEsteMovil());
    setAlAbrir(pedirHuellaAlAbrir());
  }

  async function quitar(id: string) {
    const res = await send('huella-borrar', { id });
    if (res.error) {
      setError(mensaje('sin-conexion'));
      return;
    }
    await cargar();
  }

  return (
    <View style={{ gap: 14 }}>
      <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
        <Trans>
          Sign in with your fingerprint instead of typing the password. Your
          fingerprint never leaves the phone.
        </Trans>
      </Text>
      <Interruptor
        id="huella-este-movil"
        etiqueta={t('Sign in with fingerprint on this device')}
        activo={idLocal !== null}
        deshabilitado={ocupado}
        onCambio={v => void alternarHuella(v)}
      />
      <Interruptor
        id="huella-al-abrir"
        etiqueta={t('Ask for fingerprint when opening Rumbo')}
        activo={alAbrir}
        deshabilitado={idLocal === null}
        onCambio={v => {
          if (v) {
            setEligiendoPin(true);
            return;
          }
          cambiarPedirHuellaAlAbrir(false);
          setAlAbrir(pedirHuellaAlAbrir());
        }}
      />
      {alAbrir && (
        <Enlace onPress={() => setEligiendoPin(true)}>
          <Trans>Change PIN</Trans>
        </Enlace>
      )}
      {eligiendoPin && (
        <CrearPin
          onHecho={() => {
            cambiarPedirHuellaAlAbrir(true);
            setAlAbrir(pedirHuellaAlAbrir());
            setEligiendoPin(false);
          }}
          onCancelar={() => setEligiendoPin(false)}
        />
      )}
      {error && (
        <Text role="alert" style={{ ...texto.secundario, color: color.bad }}>
          {error}
        </Text>
      )}
      {otras.length > 0 && (
        <View style={{ gap: 6 }}>
          <Text
            style={{ ...texto.secundario, fontWeight: 700, color: color.fg2 }}
          >
            <Trans>Other devices</Trans>
          </Text>
          {otras.map(h => (
            <View
              key={h.id}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <Text style={{ ...texto.cuerpo, color: color.fg }}>
                {h.nombre}
              </Text>
              <Enlace peligro onPress={() => void quitar(h.id)}>
                <Trans>Remove</Trans>
              </Enlace>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

function Interruptor({
  id,
  etiqueta,
  activo,
  deshabilitado,
  onCambio,
}: {
  id: string;
  etiqueta: string;
  activo: boolean;
  deshabilitado?: boolean;
  onCambio: (activo: boolean) => void;
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        minHeight: 44,
        opacity: deshabilitado ? 0.5 : 1,
      }}
    >
      <label htmlFor={id} style={{ ...texto.cuerpo, color: color.fg }}>
        {etiqueta}
      </label>
      <Toggle
        id={id}
        isOn={activo}
        isDisabled={deshabilitado}
        onToggle={onCambio}
      />
    </View>
  );
}
